import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { immediateToast } from "izitoast-react";
import {
  createInternalEntitlement,
  createInternalEntitlementRequest,
  getEntitlement,
  getEntitlementRequest,
  updateEntitlement,
  updateEntitlementRequest
} from "src/network/graphql/entitlementService";
import { organisationFilter, OrganisationFilterType } from "src/network/graphql/organisationService";
import { listSubscription, SubsListRequest } from "src/network/graphql/subscriptionServices";
import { orgList } from "../organisation/organisationSlice";
import { SubsList } from "../plans/plansSlice";

export type Entitlement = {
  organisation_id: string;
  order_id: string;
  entitlement_id: string;
  display_id: string;
  object_id: string;
  object_type: string;
  status: string;
  valid_untill: string;
  valid_from: string;
  operated_by: string;
  is_provider_subscription_active: boolean;
};

const entitlementSlice = createSlice({
  name: "entitlement",
  initialState: {
    loading: false,
    error: null,
    entitlements: {
      list: [] as Entitlement[],
      total: 0
    },
    organisations: {
      list: [] as orgList[],
      total: 0
    },
    subscriptions: {
      list: [] as SubsList[],
      total: 0
    },
    organisationsLoading: false,
    subscriptionsLoading: false,
    createLoading: false,
    updateLoading: false
  },
  reducers: {
    fetchEntitlementsStart: (state) => {
      state.loading = true;
    },
    fetchEntitlementsSuccess: (state, action) => {
      state.loading = false;
      state.entitlements = action.payload;
    },
    fetchEntitlementsFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOrganisationsStart: (state) => {
      state.organisationsLoading = true;
    },
    fetchOrganisationsSuccess: (state, action) => {
      state.organisationsLoading = false;
      state.organisations.list = action.payload?.list ?? [];
      state.organisations.total = action.payload?.total ?? 0;
    },
    fetchOrganisationsFail: (state, action) => {
      state.organisationsLoading = false;
      state.organisations.list = [];
      state.organisations.total = 0;
      state.error = action.payload;
    },
    fetchSubscriptionsStart: (state) => {
      state.subscriptionsLoading = true;
    },
    fetchSubscriptionsSuccess: (state, action) => {
      state.subscriptionsLoading = false;
      state.subscriptions.list = action.payload?.list ?? [];
      state.subscriptions.total = action.payload?.total ?? 0;
    },
    fetchSubscriptionsFail: (state, action) => {
      state.subscriptionsLoading = false;
      state.subscriptions.list = [];
      state.subscriptions.total = 0;
      state.error = action.payload;
    },
    createEntitlementStart: (state) => {
      state.createLoading = true;
    },
    createEntitlementSuccess: (state) => {
      state.createLoading = false;
    },
    createEntitlementFail: (state, action) => {
      state.createLoading = false;
      state.error = action.payload;
    },
    updateEntitlementStart: (state) => {
      state.updateLoading = true;
    },
    updateEntitlementSuccess: (state) => {
      state.updateLoading = false;
    },
    updateEntitlementFail: (state, action) => {
      state.updateLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchEntitlementsStart,
  fetchEntitlementsSuccess,
  fetchEntitlementsFail,
  fetchOrganisationsStart,
  fetchOrganisationsSuccess,
  fetchOrganisationsFail,
  fetchSubscriptionsStart,
  fetchSubscriptionsSuccess,
  fetchSubscriptionsFail,
  createEntitlementStart,
  createEntitlementSuccess,
  createEntitlementFail,
  updateEntitlementStart,
  updateEntitlementSuccess,
  updateEntitlementFail
} = entitlementSlice.actions;

export const fetchEntitlementsAction = (data: getEntitlementRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchEntitlementsStart());
    try {
      const response = await getEntitlement(data);
      const structuredResponse = response.data.internal_search_entitlement.items;

      dispatch(
        fetchEntitlementsSuccess({
          list: structuredResponse,
          total: response.data.internal_search_entitlement.total_size
        })
      );
    } catch (error) {
      dispatch(fetchEntitlementsFail(error));
    }
  };
};

export const fetchOrganisationsAction = (data: OrganisationFilterType) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchOrganisationsStart());
    try {
      const response = await organisationFilter(data);

      const structuredResponse = response.data.search_organisation.organisation_listing.map((org: any) => ({
        organisationId: org.organisation_id,
        name: org.name,
        id: org.display_id,
        type: org.type,
        abn: org.abn
      }));

      dispatch(
        fetchOrganisationsSuccess({
          list: structuredResponse,
          total: response.data.search_organisation.total_size
        })
      );
    } catch (error) {
      dispatch(fetchOrganisationsFail(error));
    }
  };
};

export const fetchSubscriptionsAction = (data: SubsListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchSubscriptionsStart());
    try {
      const response = await listSubscription(data);

      const structuredResponse =
        response.data.search_subscriptions.items?.map((sub: any) => ({
          subsId: sub.subscription_id,
          title: sub.title,
          subTitle: sub.subtitle,
          description: sub.description,
          date: sub.created_at,
          orgType: sub.organisation_type,
          status: sub.is_active,
          subscription_category: sub?.subscription_category,
          level: sub.level,
          attributes: sub.attributes
        })) || [];

      dispatch(
        fetchSubscriptionsSuccess({
          list: structuredResponse,
          total: response.data.search_subscriptions.total_size
        })
      );
    } catch (error) {
      dispatch(fetchSubscriptionsFail(error));
    }
  };
};

export const createEntitlementAction = (data: createInternalEntitlementRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createEntitlementStart());
    try {
      await createInternalEntitlement(data);

      dispatch(createEntitlementSuccess());
      immediateToast("success", {
        message: "Entitlement created successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(createEntitlementFail(error));
    }
  };
};

export const updateEntitlementAction = (data: updateEntitlementRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateEntitlementStart());
    try {
      await updateEntitlement(data);
      dispatch(updateEntitlementSuccess());
      immediateToast("success", {
        message: "Entitlement updated successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(updateEntitlementFail(error));
    }
  };
};

export default entitlementSlice.reducer;
